import type { SortingState } from '@tanstack/react-table';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';

import { SlideOut } from '~/components/SlideOut/SlideOut';
import { Spinner } from '~/components/Spinner';
import { ArtistHeader } from '~/modules/artist-roster/components/ArtistHeader/ArtistHeader';
import { TrackList } from '~/modules/artist-roster/components/TrackList/TrackList';
import { useReleasesUrlParams } from '~/modules/artist-roster/hooks/useReleasesUrlParams';
import type { ReleasesFilters } from '~/modules/artist-roster/hooks/useReleasesUrlParams/types';
import type { ArtistDetails } from '~/modules/artist-roster/lib/types';

import { ArtistSearchBar } from '../ArtistSearchBar/ArtistSearchBar';
import { FilterButton } from '../Filtering/FilterButton';
import type { FiltersMetadata } from '../Filtering/types';
import { TrackListHeader } from '../TrackList/TrackListHeader';
import { TrackAppliedFilterList } from '../TracksFilter/TrackAppliedFilter/TrackAppliedFilterList';
import { TrackFilterPanel } from '../TracksFilter/TrackFilterPanel';

export function ArtistReleasesPage() {
  const { t } = useTranslation('roster');
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const { filters, setFilters, updateFilters } = useReleasesUrlParams();
  const { searchText, releaseType, role } = filters;

  const updateFiltersPersist = useCallback(
    (newFilters: Partial<ReleasesFilters>) => {
      updateFilters(newFilters);
    },
    [updateFilters],
  );

  const { artistData, scrollUpCallback } = useOutletContext<{
    artistData: ArtistDetails;
    scrollUpCallback: () => void;
  }>();

  const [sortingState, setSortingState] = useState<SortingState>([
    {
      id: 'initialReleaseDate',
      desc: true,
    },
  ]);

  const filtersMetadata: FiltersMetadata = useMemo(() => {
    return {
      areFiltersApplied: Boolean(filters.releaseType || filters.role),
      filterCount: (filters.releaseType ? 1 : 0) + (filters.role ? 1 : 0),
    };
  }, [filters]);

  const handleApplyFilters = useCallback(
    (appliedFilters: ReleasesFilters) => {
      setFilters({
        searchText,
        releaseType: appliedFilters.releaseType,
        role: appliedFilters.role,
      });
    },
    [searchText, setFilters],
  );

  const handleShowFilters = useCallback(
    (isOpen: boolean) => {
      setIsFilterPanelOpen(isOpen);
    },
    [setIsFilterPanelOpen],
  );

  const handleSubmitSearch = useCallback(
    (searchValue: string) => {
      updateFiltersPersist({ searchText: searchValue });
    },
    [updateFiltersPersist],
  );

  const handleClearFilters = useCallback(() => {
    handleApplyFilters({
      searchText: undefined,
      releaseType: undefined,
      role: undefined,
    });
  }, [handleApplyFilters]);

  const handleSearchInputClear = useCallback(() => {
    updateFiltersPersist({ searchText: undefined });
  }, [updateFiltersPersist]);

  const handleRemoveReleaseTypeFilter = useCallback(() => {
    updateFiltersPersist({
      releaseType: undefined,
    });
  }, [updateFiltersPersist]);

  const handleRemoveRoleFilter = useCallback(() => {
    updateFiltersPersist({
      role: undefined,
    });
  }, [updateFiltersPersist]);

  if (!artistData) {
    return (
      <div className="flex w-full items-center justify-center p-4xl">
        <Spinner />
      </div>
    );
  }

  return (
    <>
      <ArtistHeader {...{ artistData }}>
        <div className="mb-md flex gap-md" role="search">
          <ArtistSearchBar
            initialSearchText={searchText}
            placeholder={t('filters.searchForTrack')}
            onSubmit={handleSubmitSearch}
            onClear={handleSearchInputClear}
          />
          <FilterButton
            filtersMetadata={filtersMetadata}
            onShowFilters={handleShowFilters}
          />
        </div>
        {releaseType || role ? (
          <TrackAppliedFilterList
            releaseType={releaseType}
            role={role}
            onRemoveReleaseType={handleRemoveReleaseTypeFilter}
            onRemoveRole={handleRemoveRoleFilter}
            onClear={handleClearFilters}
          />
        ) : null}
        <TrackListHeader
          uaid={artistData.uaid}
          artistData={artistData}
          columnSort={sortingState[0]}
          scrollUpCallback={scrollUpCallback}
        />
      </ArtistHeader>
      <TrackList
        uaid={artistData.uaid}
        artistData={artistData}
        columnSort={sortingState[0]}
        setSortingState={setSortingState}
      />
      <SlideOut
        {...{
          open: isFilterPanelOpen,
          onOpenChange: setIsFilterPanelOpen,
        }}
      >
        <TrackFilterPanel
          originalFilters={filters}
          onApplyFilters={handleApplyFilters}
        />
      </SlideOut>
    </>
  );
}

ArtistReleasesPage.displayName = 'ArtistReleasesPage';
